import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import firebase from 'src/utils/firebase';
import axios from 'axios';

const PAYROLL_API_BASE_URL = 'https://redline-autohaus-api.herokuapp.com';

const extractErrorMessage = (error, fallbackMessage) =>
  error?.response?.data?.message || error?.message || fallbackMessage;

const initialState = {
  techs: [],
  timeTable: {},
  prevTimeTable: {},
  lastClock: [],
  loading: false,
  initialized: false,
  refreshing: false,
  error: null,
  clockInLoading: false,
  clockOutLoading: false,
  actionError: null
};

export const fetchPayrollData = createAsyncThunk(
  'payroll/fetchPayrollData',
  async function (_, { rejectWithValue }) {
    try {
      const idToken = await firebase.auth().currentUser.getIdToken();
      const [clockResponse, clockResponsePrevWeek] = await Promise.all([
        axios.get(`${PAYROLL_API_BASE_URL}/employees/hours/calculate/${idToken}`),
        axios.get(`${PAYROLL_API_BASE_URL}/employees/hours/prev/calculate/${idToken}`)
      ]);

      return {
        techs: clockResponse.data.employees || [],
        timeTable: clockResponse.data.timeTable || {},
        prevTimeTable: clockResponsePrevWeek.data.timeTable || {},
        lastClock: clockResponse.data.lastClock || []
      };
    } catch (error) {
      return rejectWithValue(extractErrorMessage(error, 'Unable to load payroll data'));
    }
  }
);

export const clockIn = createAsyncThunk(
  'payroll/clockIn',
  async function (technician, { dispatch, rejectWithValue }) {
    try {
      const idToken = await firebase.auth().currentUser.getIdToken();
      const response = await axios.post(`${PAYROLL_API_BASE_URL}/clock/in`, {
        technician,
        idToken
      });

      await dispatch(fetchPayrollData()).unwrap();

      return {
        technician,
        message: response?.data?.message || `Clocked in ${technician}`
      };
    } catch (error) {
      return rejectWithValue(extractErrorMessage(error, 'Unable to clock in technician'));
    }
  }
);

export const clockOut = createAsyncThunk(
  'payroll/clockOut',
  async function (technician, { dispatch, rejectWithValue }) {
    try {
      const idToken = await firebase.auth().currentUser.getIdToken();
      const response = await axios.post(`${PAYROLL_API_BASE_URL}/clock/out`, {
        technician,
        idToken
      });

      await dispatch(fetchPayrollData()).unwrap();

      return {
        technician,
        message: response?.data?.message || `Clocked out ${technician}`
      };
    } catch (error) {
      return rejectWithValue(extractErrorMessage(error, 'Unable to clock out technician'));
    }
  }
);

const slice = createSlice({
  name: 'payroll',
  initialState,
  reducers: {
    clearPayrollActionError(state) {
      state.actionError = null;
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchPayrollData.pending, (state) => {
        if (!state.initialized) {
          state.loading = true;
        } else {
          state.refreshing = true;
        }
        state.error = null;
      })
      .addCase(fetchPayrollData.fulfilled, (state, action) => {
        state.loading = false;
        state.refreshing = false;
        state.initialized = true;
        state.error = null;
        state.techs = action.payload.techs;
        state.timeTable = action.payload.timeTable;
        state.prevTimeTable = action.payload.prevTimeTable;
        state.lastClock = action.payload.lastClock;
      })
      .addCase(fetchPayrollData.rejected, (state, action) => {
        state.loading = false;
        state.refreshing = false;
        state.error = action.payload || action.error?.message || 'Unable to load payroll data';
      })
      .addCase(clockIn.pending, (state) => {
        state.clockInLoading = true;
        state.actionError = null;
      })
      .addCase(clockIn.fulfilled, (state) => {
        state.clockInLoading = false;
        state.actionError = null;
      })
      .addCase(clockIn.rejected, (state, action) => {
        state.clockInLoading = false;
        state.actionError = action.payload || action.error?.message || 'Unable to clock in technician';
      })
      .addCase(clockOut.pending, (state) => {
        state.clockOutLoading = true;
        state.actionError = null;
      })
      .addCase(clockOut.fulfilled, (state) => {
        state.clockOutLoading = false;
        state.actionError = null;
      })
      .addCase(clockOut.rejected, (state, action) => {
        state.clockOutLoading = false;
        state.actionError = action.payload || action.error?.message || 'Unable to clock out technician';
      });
  }
});

export const { clearPayrollActionError } = slice.actions;
export const reducer = slice.reducer;
